import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { layout } from '@repo/ui';
import { YearProvider } from '../app/year';
import { CommandPalette } from './CommandPalette';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import { SuspenseOutlet } from '../routes/lazy';

/** Back-office shell: 64px top bar + collapsible side nav + scrolling main area. */
export function AppShell() {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [navOpen, setNavOpen] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <YearProvider>
      <Box
        component="a"
        href="#main"
        sx={{
          position: 'absolute',
          insetInlineStart: 8,
          top: -48,
          zIndex: 2000,
          bgcolor: 'background.paper',
          p: 1,
          borderRadius: 1,
          '&:focus': { top: 8 },
        }}
      >
        {t('shell.skipToContent')}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: `${layout.topBarHeight}px 1fr`,
          // minmax(0, 1fr): wide pages (jury grid) scroll inside <main> instead of widening the shell.
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridTemplateAreas: '"top" "body"',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <TopBar
          compact={mobile}
          onToggleNav={() => (mobile ? setDrawer(true) : setNavOpen((o) => !o))}
          onOpenSearch={() => setPaletteOpen(true)}
        />
        <Box sx={{ gridArea: 'body', display: 'flex', minHeight: 0 }}>
          {mobile ? (
            <Drawer
              open={drawer}
              onClose={() => setDrawer(false)}
              anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            >
              <SideNav open onNavigate={() => setDrawer(false)} />
            </Drawer>
          ) : (
            <SideNav open={navOpen} />
          )}
          <Box
            component="main"
            id="main"
            tabIndex={-1}
            sx={{ flex: 1, minWidth: 0, overflow: 'auto', pb: 10, outline: 'none' }}
          >
            <SuspenseOutlet />
          </Box>
        </Box>
      </Box>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </YearProvider>
  );
}

/** Standard page container: 24/32px padding, 1440px max width (design). */
export function Page({
  children,
  maxWidth = layout.contentMaxWidth,
}: {
  children: React.ReactNode;
  maxWidth?: number;
}) {
  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        maxWidth,
        boxSizing: 'border-box',
      }}
    >
      {children}
    </Box>
  );
}
